import { toJobOfferBaseType } from '@backend/app-acquisition-logic';

import type { JobOffer } from '~/business/JobOffer/types';
import type { Translator } from '~/utils/translations/buildTranslate';

// Google cuts a description past about 155 characters
const MAX_DESCRIPTION_LENGTH = 155;

// the work rates come as the list acquisition renders on the page: "<p>Work schedule:</p><ul><li>Night work</li></ul>"
const getWorkRates = (workRatesHTML: string | null | undefined) =>
  [...(workRatesHTML ?? '').matchAll(/<li[^>]*>([\s\S]*?)<\/li>/g)]
    .map(([, workRate]) =>
      workRate
        .replace(/<[^>]+>/g, '')
        .replace(/\s+/g, ' ')
        .trim(),
    )
    .filter(Boolean);

/**
 * The offer page's meta description. Offers for the same job in the same city are common (one per client, one per
 * shift): the pay, the hours and the work schedule tell them apart, so they come before the call to action, which
 * only follows when it fits.
 */
export const getMissionMetaDescription = (
  t: Translator,
  jobOffer: Pick<JobOffer, 'type' | 'title'> & {
    location: { city: string };
    salaryFormattedCurrency?: string | null;
    salaryFormattedUnit?: string | null;
    weeklyHours?: number | null;
    workRatesHTML?: string | null;
  },
) => {
  const type = toJobOfferBaseType(jobOffer.type).toLowerCase();
  const params = { title: jobOffer.title, city: jobOffer.location.city };
  // an external offer's type is free text: one with no sentence of its own still names its job and city
  const summary = t(`meta_description.jobOffer.type.${type}`, params) || t('meta_description.jobOffer.default', params);

  const details = [
    jobOffer.salaryFormattedCurrency &&
      jobOffer.salaryFormattedUnit &&
      `${jobOffer.salaryFormattedCurrency} ${jobOffer.salaryFormattedUnit}`,
    jobOffer.weeklyHours && t('missions.weekly_hours.default', { hours: jobOffer.weeklyHours }),
    ...getWorkRates(jobOffer.workRatesHTML),
  ].filter(Boolean);

  // a list rather than a sentence: each detail keeps the case it has on the page ("Brut/heure", "Saturday work")
  const detailsList = details.join(' · ');
  const detailsSentence = detailsList && !detailsList.endsWith('.') ? `${detailsList}.` : detailsList;
  const description = detailsSentence ? `${summary} ${detailsSentence}` : summary;
  const withCallToAction = `${description} ${t('meta_description.jobOffer.suffix')}`;

  return withCallToAction.length <= MAX_DESCRIPTION_LENGTH ? withCallToAction : description;
};
